import React, { useState } from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import CookieDisclaimer from "../components/CookieDisclaimer";
import SEO from "../components/SEO";
import "../styles.css";
import videoData from '../data/videos.json';

export default function Videos() {
  // Only show videos from 2020 and newer
  const filteredByYear = videoData.filter(video => {
    const year = new Date(video.snippet.publishedAt).getFullYear();
    return year >= 2020;
  });
  const [visibleCount, setVisibleCount] = useState(9);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const staticCategories = [
    "All Videos",
    "Tutorials", 
    "Gameplay"
  ];
  const [selectedCategory, setSelectedCategory] = useState("Tutorials");

  function getCategory(title, description) {
    // Gameplay videos with "| Part " in title - prioritize this first
    if (title.includes("| Part ")) return "Gameplay";
    // Check title for general tutorial keywords (but exclude gameplay videos)
    if (/tutorial|how to|guide|setup|Streamer\.bot|walkthrough|plugin|tool|Built|Customizer|ULTIMATE|DEATH.*COUNTER|COUNTER.*DEATH|zoom|track|mouse|follow/i.test(title)) return "Tutorials";
    // Check description for specific tutorial indicators
    if (/step-by-step|In this.*tutorial|zoom.*mouse|track.*mouse|follow.*mouse/i.test(description)) return "Tutorials";
    // Check for general gameplay keywords in title
    if (/gameplay|playthrough|let's play|match|session|[PC]/i.test(title)) return "Gameplay";
    return null;
  }

  let displayVideos = filteredByYear;
  if (selectedCategory !== "All Videos") {
    displayVideos = displayVideos.filter(video => getCategory(video.snippet.title, video.snippet.description) === selectedCategory);
  }
  if (searchTerm) {
    displayVideos = displayVideos.filter(video =>
      video.snippet.title.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }
  const paginatedVideos = displayVideos.slice(0, visibleCount);

  function handleLoadMore() {
    setVisibleCount(v => v + 9);
  }

  return (
    <>
      <SEO
        title="YouTube Videos"
        description="Watch ZeMaxi's YouTube videos including gaming tutorials, Streamer.bot guides, OBS setup walkthroughs, and gameplay highlights. Learn streaming tips and tricks!"
        keywords="ZeMaxi YouTube, streaming tutorials, Streamer.bot tutorials, OBS guides, gaming videos, gameplay highlights, streaming tips"
        url="https://zemaxi.com/videos"
      />
      <Navbar />
      <CookieDisclaimer />
      <main>
        <section className="videos-hero noise-bg">
          <div className="container">
            <h1>YouTube <span className="highlight">Videos</span></h1>
            <p>Watch my latest YouTube videos and highlights!</p>
          </div>
        </section>
        <section className="videos-section">
          <div className="container videos-container">
            <div className="games-search">
              <div className="search-filter-row">
                <div className="search-container">
                  <i className="fas fa-search search-icon"></i>
                  <input
                    type="text"
                    placeholder="Search videos..."
                    value={searchTerm}
                    onChange={(e) => { setSearchTerm(e.target.value); setVisibleCount(9); }}
                    className="search-input"
                  />
                  {searchTerm && (
                    <button
                      className="search-clear"
                      onClick={() => setSearchTerm("")}
                      aria-label="Clear search"
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  )}
                </div>
                <div className="category-dropdown-wrapper">
                  <select
                    className="category-dropdown"
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                  >
                    {staticCategories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                  <i className="fas fa-chevron-down category-dropdown-arrow"></i>
                </div>
              </div>
            </div>
            <div className="videos-grid">
              {loading && (
                <div className="video-loading">
                  <div className="spinner"></div>
                  <p>Loading videos...</p>
                </div>
              )}
              {error && (
                <div className="video-loading">
                  <p style={{ color: 'red' }}>Failed to load videos: {error}</p>
                </div>
              )}
              {!loading && !error && paginatedVideos.length === 0 && (
                <div className="video-loading">
                  <p>No videos found.</p>
                </div>
              )}
              {!loading && !error && paginatedVideos.map(video => {
                const catMatch = video.snippet.title.match(/\[(.*?)\]/);
                const category = catMatch && catMatch[1] ? catMatch[1].trim() : null;
                return (
                  <div className="video-item" key={video.id.videoId}>
                    <a href={`https://www.youtube.com/watch?v=${video.id.videoId}`} target="_blank" rel="noopener noreferrer">
                      <div className="video-thumbnail">
                        <img src={video.snippet.thumbnails.high.url} alt={video.snippet.title} />
                        <div className="play-button"><i className="fas fa-play"></i></div>
                      </div>
                      <div className="video-info">
                        <h3 dangerouslySetInnerHTML={{ __html: video.snippet.title }}></h3>
                        <div className="video-meta">
                          <span>{new Date(video.snippet.publishedAt).toLocaleDateString()}</span>
                        </div>
                        {category && (
                          <div className="video-tags">
                            <span className="video-tag">{category}</span>
                          </div>
                        )}
                      </div>
                    </a>
                  </div>
                );
              })}
            </div>
            {!loading && !error && paginatedVideos.length < displayVideos.length && (
              <div className="load-more-container">
                <button className="btn btn-primary" onClick={handleLoadMore}>Load More</button>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
